import { DietPickerElement } from 'components/molecules/FormElements/DietPickerElement/DietPickerElement';
import { ChildrenMealSelection } from 'components/templates/child/ChildAddStepsForm/components/Step3/components/ChildrenMealsElement';
import { formFields } from 'consts/form/formFields';
import { AppFormContext } from 'contexts/AppFormContext';
import { useDisplayFetchDataError } from 'hooks/useDisplayFetchDataError/useDisplayFetchDataError';
import { useCurrentInstitution } from 'jotaiAtoms/currentInstitution';
import { useWatch } from 'rc-field-form';
import { useContext, useEffect, useMemo } from 'react';
import { useAppQuery } from 'services/reactQuery/useAppQuery';
import { ChildDietDTO, ChildId, DietDTO, DietId, MealTypeDTO, MealTypeId } from 'types';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import { AxiosResponse } from 'axios';
import { useParams } from 'react-router-dom';
import { commonTexts } from '../../../../consts/text';
import { SimpleDatePicker } from '../../../molecules';

type DietPickerProps = {
    httpMethod: {
        getDiets: () => Promise<AxiosResponse<DietDTO[]>>;
        getMealTypes: () => Promise<AxiosResponse<MealTypeDTO[]>>;
        getChildDiet?: (childId: ChildId) => Promise<AxiosResponse<ChildDietDTO>>;
    };
};
export const DietPicker = ({ httpMethod }: DietPickerProps) => {
    const form = useFormInstance();
    const currentInstitution = useCurrentInstitution();
    const { formError, setFormError } = useContext(AppFormContext);
    const dietPickerValue = useWatch<DietId | undefined>(formFields.diet, form);
    const dietValue = useWatch<DietId | undefined>(formFields.diet, form);
    const { childId: childIdParam } = useParams();

    const childId = (childIdParam || '') as ChildId;

    const {
        data: dietsData,
        isFetching: isDietsDataFetching,
        isError: isDietsDataError,
    } = useAppQuery('DIETS', [currentInstitution.id], httpMethod.getDiets, {});

    const {
        data: mealTypesData,
        isFetching: isMealTypesDataFetching,
        isError: isMealTypesDataError,
    } = useAppQuery('MEAL_TYPES', [currentInstitution.id], httpMethod.getMealTypes, {});

    const {
        data: childDietData,
        isFetching: _isChildDietDataFetching,
        isError: _isChildDietDataError,
    } = useAppQuery(
        'CHILD_DIET',
        [currentInstitution.id, childId],
        () =>
            httpMethod.getChildDiet?.(childId) ??
            Promise.reject(new Error('getChildDiet not available')),
        {
            enabled: !!dietPickerValue && !!httpMethod.getChildDiet,
        },
    );

    useDisplayFetchDataError({
        isFetching: isDietsDataFetching || isMealTypesDataFetching,
        isError: isDietsDataError || isMealTypesDataError,
        actualError: formError,
        errorMessage: 'Nie udało się pobrać danych o dietach lub typach posiłków',
        setError: setFormError,
    });

    const options = useMemo(() => {
        if (!dietsData) {
            return [];
        }
        return dietsData.map((diet) => ({
            value: diet.id,
            label: diet.name,
        }));
    }, [dietsData]);

    const selectedDiet = dietsData?.find((diet) => diet.id === dietPickerValue);

    // Map mealPrices array to the expected format for ChildrenMealSelection
    // and order them according to the mealTypes order
    const mealsForDiet = useMemo(() => {
        if (!selectedDiet?.meals || !mealTypesData) {
            return [];
        }

        // Create a map for quick lookup of meal type order
        const mealTypeOrderMap = new Map(
            mealTypesData.map((mealType, index) => [mealType.id, index]),
        );

        const currentMealPrices = selectedDiet.meals[selectedDiet.meals.length - 1];

        return currentMealPrices.mealPrices
            .map((meal) => ({
                ...meal,
                order:
                    mealTypeOrderMap.get(meal.mealTypeId as MealTypeId) ?? Number.MAX_SAFE_INTEGER,
            }))
            .sort((a, b) => a.order - b.order)
            .filter((meal) => meal.price)
            .map(({ order: _order, ...meal }) => ({
                ...meal,
                dateFrom:
                    Math.min(
                        ...selectedDiet.meals
                            .filter((entry) =>
                                entry.mealPrices.some(
                                    (mp) =>
                                        mp.mealTypeId === meal.mealTypeId &&
                                        mp.price === meal.price,
                                ),
                            )
                            .map((entry) => new Date(entry.dateFrom).getTime()),
                    ) === Infinity
                        ? undefined
                        : new Date(
                              Math.min(
                                  ...selectedDiet.meals
                                      .filter((entry) =>
                                          entry.mealPrices.some(
                                              (mp) =>
                                                  mp.mealTypeId === meal.mealTypeId &&
                                                  mp.price === meal.price,
                                          ),
                                      )
                                      .map((entry) => new Date(entry.dateFrom).getTime()),
                              ),
                          )
                              .toISOString()
                              .split('T')[0],
            }));
    }, [selectedDiet, mealTypesData]);

    useEffect(() => {
        if (childDietData) {
            // Only set values if form doesn't already have them (preserve initial values when editing)
            const currentMealTypes = form.getFieldValue(formFields.selectMealsType);
            if (!currentMealTypes || currentMealTypes.length === 0) {
                form.setFieldValue(
                    formFields.selectMealsType,
                    childDietData.meals.map((meal) => meal.mealTypeId),
                );
            }
        }
    }, [childDietData, form]);

    return (
        <>
            <DietPickerElement
                options={options}
                loading={isDietsDataFetching || isMealTypesDataFetching}
                onChange={() => form.setFieldValue(formFields.selectMealsType, null)}
            />
            <SimpleDatePicker
                formItemLabel={commonTexts.dataLabels.pricesDateFrom}
                name={formFields.dietFromDate}
                optional={!dietValue}
                placeholder="Wybierz datę od"
            />
            {dietPickerValue && <ChildrenMealSelection meals={mealsForDiet} />}
        </>
    );
};
